import { Component, Inject, PLATFORM_ID, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { compare, Operation } from 'fast-json-patch';
import { firstValueFrom, Observable, of, Subject, takeUntil } from 'rxjs';
import { TeamDetailsDto } from '../../../../core/models/teams/team-details-dto';

import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ConfirmDialogComponent } from '../../../../core/components/confirm-dialog/confirm-dialog.component';
import { EntityRelationChangeTransactionDetailsComponent } from '../../../../core/components/entity-relation-change-transaction-details/entity-relation-change-transaction-details.component';
import { SaveChangesDialogComponent, SaveChangesResult } from '../../../../core/components/save-changes-dialog/save-changes-dialog.component';
import { EditablePostClientStatus } from '../../../../core/enums/editable-post-client-status';
import { ProductionTechnicianArea, ProductionTechnicianAreaLabels } from '../../../../core/enums/production-technician-area';
import { ComponentCanDeactivate } from '../../../../core/guards/pending-changes.guard';
import { ContractorLookupDto } from '../../../../core/models/contractors/contractor-lookup-dto';
import { MachineStatus } from '../../../../core/models/machines/machine-status';
import { TeamDetailsMachineInfoDto } from '../../../../core/models/teams/team-details-machine-info-dto';
import { TeamOwnerType, TeamOwnerTypeLabels } from '../../../../core/models/teams/team-owner-type';
import { TeamStatus, TeamStatusLabels } from '../../../../core/models/teams/team-status';
import { TeamType, TeamTypeLabels } from '../../../../core/models/teams/team-type';
import { UpdateTeamResponseModel } from '../../../../core/models/teams/update-team-response.model';
import { EntraIdUserModel } from '../../../../core/models/users/entra-id-user.model';
import { ContractorApiService } from '../../../../core/services/api/contractor-api.service';
import { TeamApiService } from '../../../../core/services/api/team-api.service';
import { UserApiService } from '../../../../core/services/api/user-api.service';
import { AuthService } from '../../../../core/services/auth.service';
import { IconService } from '../../../../core/services/icon.service';
import { NavigationService } from '../../../../core/services/navigation.service';
import { TeamAddMachineDialogComponent } from '../team-add-machine-dialog/team-add-machine-dialog.component';

@Component({
  selector: 'app-team-details',
  imports: [MatIconModule, CommonModule, MatFormFieldModule, FontAwesomeModule, MatTooltipModule, MatInputModule, MatSelectModule, FormsModule,
    MatButtonModule, ReactiveFormsModule, MatCheckboxModule, MatOptionModule, MatProgressSpinnerModule, MatListModule,
    MatTableModule, MatSortModule, RouterModule, EntityRelationChangeTransactionDetailsComponent],
  templateUrl: './team-details.component.html',
  styleUrl: './team-details.component.scss'
})
export class TeamDetailsComponent implements ComponentCanDeactivate {
  public team!: TeamDetailsDto;

  private destroy$ = new Subject<void>();
  private baselineUpdateModel?: UpdateTeamModel;
  showDeactivatedCheckbox = new FormControl(false);
  teamForm!: FormGroup<any>;
  isLoading: any;
  teamHasChanged: boolean = false;
  formChanges: any;
  initialValue: any;
  allContractors: ContractorLookupDto[] = [];
  allProductionLeaders: EntraIdUserModel[] = [];

  teamStatusLabels = TeamStatusLabels;
  statusEnum = Object.values(TeamStatus).filter(x => typeof x === "number");
  teamOwnerTypeLabels = TeamOwnerTypeLabels;
  teamOwnerTypeEnum = Object.values(TeamOwnerType).filter(x => typeof x === "number");
  teamTypeLabels = TeamTypeLabels;
  teamTypeEnum = Object.values(TeamType).filter(x => typeof x === "number");
  productionTechnicianAreaLabels = ProductionTechnicianAreaLabels;
  productionTechnicianAreaEnum = Object.values(ProductionTechnicianArea).filter(x => typeof x === "number");
  allTeamIds: string[] = [];

  teamMachinesDataSource = new MatTableDataSource<TeamDetailsMachineInfoDto>([]);

  teamMachineColumns = ['machineId', 'statusText', 'machineManufacturerText', 'modelText', 'machineTypeText'];
  clientStatus = EditablePostClientStatus;
  machineStatus = MachineStatus;
  isPT: boolean = false;
  isReadonly: boolean = true;
  isNew: boolean = false;
  showStoredProductionLeaderName: boolean = true;
  showStoredContractorName: boolean = true;

  @ViewChild(MatSort) sort: MatSort | undefined;

  constructor(private teamApiService: TeamApiService,
    private contractorApiService: ContractorApiService,
    private userApiService: UserApiService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    public authService: AuthService,
    private dialog: MatDialog,
    private navigationService: NavigationService,
    public iconService: IconService,

    @Inject(PLATFORM_ID) private platformId: Object) {
  }
  async ngOnInit(): Promise<void> {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(async (params) => {
      const teamId = params.get('teamId');

      if (teamId === '0') {
        this.teamApiService.getTeamList().subscribe((teams) => {
          this.allTeamIds = teams.map(team => team.teamId);
        });
        this.createNewTeam();
      } else {
        await this.loadTeam();
      }

      this.isPT = this.authService.isPolicyPT();
      if (!this.isPT) {
        this.teamForm.disable();
      } else {
        this.teamMachineColumns.push("action-c");
      }

      await this.loadContractors();
      await this.loadProductionLeaders();

      this.isReadonly = !this.authService.isPolicyPT();
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.setupTableSorting();
    }
  }

  private setupTableSorting() {
    this.teamMachinesDataSource.sort = this.sort!;
    this.teamMachinesDataSource.sortingDataAccessor = (item: any, property: string) => {
      if (property) return (item as any)[property]?.toLowerCase?.() ?? '';
      return '';
    };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadContractors() {
    this.allContractors = await firstValueFrom(this.contractorApiService.getContractorLookups());
    this.updateShowStoredContractorName();
  }

  async loadProductionLeaders() {
    this.allProductionLeaders = await firstValueFrom(this.userApiService.getAllProductionLeadersList());
    this.updateShowStoredProductionLeaderName();
  }

  public updateShowStoredProductionLeaderName(): void {
    if (this.team?.productionLeaderUserId && this.allProductionLeaders?.length &&
      !this.allProductionLeaders.some(c => c.userId === this.team.productionLeaderUserId)) {
      this.showStoredProductionLeaderName = true;

      return;
    }

    this.showStoredProductionLeaderName = false;
  }

  public updateShowStoredContractorName(): void {
    if (this.team?.contractorId && this.allContractors?.length &&
      !this.allContractors.some(c => c.id === this.team.contractorId)) {
      this.showStoredContractorName = true;
      return;
    }

    this.showStoredContractorName = false;
  }

  async loadTeam() {
    if (this.isLoading) return;

    this.isLoading = true;
    const idParam = this.route.snapshot.paramMap?.get('teamId');
    const id = idParam != null ? parseInt(idParam) : 0;

    if (id === 0) {
      this.isLoading = false;
      return;
    }

    this.team = await firstValueFrom(this.teamApiService.getTeam(id));
    this.team.machines = this.team.machines
      .map(machine => ({ ...machine, clientStatus: machine.clientStatus ?? EditablePostClientStatus.Updated }))
      .sort((a, b) => (b.machineId || '').localeCompare(a.machineId || ''));
    this.teamMachinesDataSource.data = [...this.team.machines];

    if (this.teamForm != null) {
      this.teamForm.reset();
      this.formChanges = null;
    }

    this.initTeamForm(false);
    this.refreshBaselineFromForm();
    this.teamForm.markAsPristine();
    this.teamHasChanged = false;
    this.validateAllFormFields();
    this.isLoading = false;

    this.updateShowStoredProductionLeaderName();
    this.updateShowStoredContractorName();
  }

  private createNewTeam(): void {
    this.team = {
      id: 0,
      teamId: '',
      responsibility: '',
      name: '',
      active: TeamStatus.Active,
      activeText: "Aktiv",
      ownerType: TeamOwnerType.Internal,
      ownerTypeText: '',
      contractorId: 0,
      contractorText: '',
      teamType: TeamType.Normal,
      teamTypeText: 'Normal',
      productionTechnicianArea: ProductionTechnicianArea.Gotaland,
      productionTechnicianAreaText: 'Götaland',
      forwardingDelayInDays: 0,
      productionLeaderUserId: '',
      productionLeaderDisplayName: '',
      machines: [],
      created: new Date().toISOString(),
      lastModified: '',
      entityRelationChangeTransactionDetails: null
    }

    this.isReadonly = false;
    this.teamMachinesDataSource.data = [];
    this.initTeamForm(true);
    this.teamForm.markAsPristine();
    this.refreshBaselineFromForm();
    this.teamHasChanged = false;
  }

  initTeamForm(isNew: boolean): void {
    if (isNew) {
      this.teamForm = this.fb.group({
        teamId: ['M', [Validators.required, Validators.pattern(/^M\d{4}$/), this.teamIdExistsValidator()]],
        responsibility: [null, [Validators.required, Validators.pattern(/^\d{4}$/)]],
        name: [null, [Validators.required]],
        active: [null, [Validators.required]],
        activeText: [null],
        ownerType: [null, [Validators.required]],
        ownerTypeText: [null],
        contractorId: [null, this.conditionalContractorValidator()],
        contractorText: [null],
        teamType: [null, [Validators.required]],
        teamTypeText: [null],
        productionTechnicianArea: [null, [Validators.required]],
        productionTechnicianAreaText: [null],
        forwardingDelayInDays: [null, [Validators.required]],
        productionLeaderUserId: [null, [Validators.required]],
        productionLeaderDisplayName: [null],
        machines: [[]],
        created: [null],
        lastModified: [null]
      });
    }
    else {
      this.teamForm = this.fb.group({
        teamId: [this.team.teamId, [Validators.required, Validators.pattern(/^M\d{4}$/), this.teamIdExistsValidator()]],
        responsibility: [this.team.responsibility, [Validators.required, Validators.pattern(/^\d{4}$/)]],
        name: [this.team.name, [Validators.required]],
        active: [this.team.active, [Validators.required]],
        activeText: [this.team.activeText],
        ownerType: [this.team.ownerType, [Validators.required]],
        ownerTypeText: [this.team.ownerTypeText],
        contractorId: [this.team.contractorId, this.conditionalContractorValidator()],
        contractorText: [this.team.contractorText],
        teamType: [this.team.teamType, [Validators.required]],
        teamTypeText: [this.team.teamTypeText],
        productionTechnicianArea: [this.team.productionTechnicianArea, [Validators.required]],
        productionTechnicianAreaText: [this.team.productionTechnicianAreaText],
        forwardingDelayInDays: [this.team.forwardingDelayInDays, [Validators.required]],
        productionLeaderUserId: [this.team.productionLeaderUserId, [Validators.required]],
        productionLeaderDisplayName: [this.team.productionLeaderDisplayName],
        machines: [this.team.machines],
        created: [this.team.created],
        lastModified: [this.team.lastModified]
      });
    }

    this.teamForm.get('ownerType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.teamForm.get('contractorId')?.updateValueAndValidity();
      });

    this.formChanges = null;

    this.initialValue = this.teamForm.value as TeamDetailsDto;
    this.teamForm.valueChanges.subscribe(currentValue => {
      const changes = this.getChanges(this.initialValue, currentValue);
      this.formChanges = changes;
      if (!this.hasAnyChanges(changes)) {
        this.teamForm.markAsPristine();
      }
    });
  }

  private refreshBaselineFromForm(): void {
    if (!this.teamForm) {
      this.baselineUpdateModel = undefined;
      return;
    }

    const rawValue = this.teamForm.getRawValue() as TeamDetailsDto;
    const currentModel = this.mapFormToUpdateCommand(rawValue);
    this.baselineUpdateModel = this.cloneUpdateModel(currentModel);
  }

  private cloneUpdateModel(model: UpdateTeamModel): UpdateTeamModel {
    return JSON.parse(JSON.stringify(model));
  }

  private createPatchOperations(precomputedCurrent?: UpdateTeamModel): { patch: Operation[]; current: UpdateTeamModel } {
    const currentModel = precomputedCurrent ?? this.mapFormToUpdateCommand(this.teamForm.getRawValue() as TeamDetailsDto);

    if (!this.baselineUpdateModel) {
      this.baselineUpdateModel = this.cloneUpdateModel(currentModel);
    }

    const patch = compare(this.baselineUpdateModel, currentModel);
    return { patch, current: currentModel };
  }

  private normalizePatchOperations(patch: Operation[]): Operation[] {
    const arrayRoots = ['/addedMachines', '/removedMachines'];

    return patch.map(operation => {
      if (operation.op === 'add') {
        const root = arrayRoots.find(pathRoot => operation.path.startsWith(pathRoot + '/'));
        if (root && !operation.path.endsWith('/-')) {
          return { ...operation, path: `${root}/-` };
        }
      }

      return operation;
    });
  }

  private onSuccessfulSave(currentModel: UpdateTeamModel): void {
    this.baselineUpdateModel = this.cloneUpdateModel(currentModel);
    this.formChanges = null;
    this.teamHasChanged = false;
    this.initialValue = this.teamForm.getRawValue() as TeamDetailsDto;
    this.teamForm.markAsPristine();
  }

  getChanges(initial: any, current: any): any {
    const changes: any = {};
    for (const key in current) {
      if (typeof current[key] === 'object' && current[key] !== null) {
        const nestedChanges = this.getChanges(initial[key], current[key]);
        if (Object.keys(nestedChanges).length > 0) {
          changes[key] = nestedChanges;
        }
      } else if (current[key] !== initial[key]) {
        changes[key] = current[key];
      }
    }
    return changes;
  }

  hasAnyChanges(changes: any): boolean {
    return Object.keys(changes).length > 0;
  }

  saveChanges() {
    const hasFormChanges = this.formChanges ? this.hasAnyChanges(this.formChanges) : false;
    if (!hasFormChanges && !this.teamHasChanged) {
      return;
    }

    const rawValue = this.teamForm.getRawValue() as TeamDetailsDto;
    const currentModel = this.mapFormToUpdateCommand(rawValue);
    const machines = this.teamMachinesDataSource.data ?? [];
    const shouldPromptDeactivateMachines = this.shouldPromptDeactivateMachines(currentModel, machines);

    const executeSave = (deactivateMachines: boolean) => {
      const saveModel: UpdateTeamModel = {
        ...currentModel,
        deactivateMachinesWithoutOtherActiveTeams: deactivateMachines
      };

      if (this.team.id === 0) {
        this.teamApiService.updateTeam(saveModel).subscribe({
          next: (response: UpdateTeamResponseModel) => {
            const baselineModel: UpdateTeamModel = {
              ...saveModel,
              deactivateMachinesWithoutOtherActiveTeams: false
            };

            this.onSuccessfulSave(baselineModel);
            this.team.id = response.id;
            this.team.teamId = response.teamId;
            this.router.navigate(['/team/' + response.id]).then(() => {
              this.loadTeam();
            });
          },
          error: (err) => {
            this.validateAllFormFields();
          }
        });
        return;
      }

      const { patch, current } = this.createPatchOperations(saveModel);
      const sanitizedCurrent: UpdateTeamModel = {
        ...current,
        deactivateMachinesWithoutOtherActiveTeams: false
      };

      if (patch.length === 0) {
        this.onSuccessfulSave(sanitizedCurrent);
        return;
      }

      const normalizedPatch = this.normalizePatchOperations(patch);
      const teamIdentifier = sanitizedCurrent.teamId ?? this.team.teamId;

      this.teamApiService.patchTeam(this.team.id, teamIdentifier, normalizedPatch).subscribe({
        next: () => {
          this.onSuccessfulSave(sanitizedCurrent);
          this.loadTeam();
        },
        error: (err) => {
          this.validateAllFormFields();
        }
      });
    };

    if (shouldPromptDeactivateMachines) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          text: `Vill du även inaktivera de maskiner som ingår i maskinlaget ${this.team?.name}?`,
          title: 'Inaktivera maskiner',
          confirmButton: 'Ja',
          cancelButton: 'Nej'
        },
        maxWidth: '90vw'
      });

      dialogRef.afterClosed().subscribe((deactivate: boolean | undefined) => {
        if (deactivate === undefined) {
          return;
        }

        executeSave(deactivate === true);
      });
      return;
    }

    executeSave(false);
  }

  async reloadMachineDetailsView() {
    await this.loadTeam();
    this.teamHasChanged = false;
  }

  mapFormToUpdateCommand(form?: TeamDetailsDto): UpdateTeamModel {
    const source = form ?? (this.teamForm?.getRawValue() as TeamDetailsDto);
    const activeStatus = (source?.active ?? this.team?.active ?? TeamStatus.Active) === TeamStatus.Active;
    const contractorIdValue = source?.contractorId ?? this.team?.contractorId ?? null;
    const contractorId = contractorIdValue === 0 ? null : contractorIdValue;
    const productionLeaderUserIdValue = source?.productionLeaderUserId ?? this.team?.productionLeaderUserId ?? '';
    const productionLeaderUserId = productionLeaderUserIdValue ? productionLeaderUserIdValue : null;
    const forwardingDelay = typeof source?.forwardingDelayInDays === 'number' ? source.forwardingDelayInDays : null;

    const machines = this.team?.machines ?? [];

    return {
      id: this.team?.id ?? 0,
      teamId: source?.teamId ?? this.team?.teamId ?? '',
      name: source?.name ?? this.team?.name ?? null,
      active: activeStatus,
      ownerType: source?.ownerType ?? this.team?.ownerType ?? TeamOwnerType.Internal,
      responsibility: source?.responsibility ?? this.team?.responsibility ?? null,
      teamType: source?.teamType ?? this.team?.teamType ?? TeamType.Normal,
      productionTechnicianArea: source?.productionTechnicianArea ?? this.team?.productionTechnicianArea ?? ProductionTechnicianArea.Gotaland,
      contractorId: contractorId,
      productionLeaderUserId: productionLeaderUserId,
      productionLeaderDisplayName: productionLeaderUserId ? (this.getDisplayNameForProductionLeaderUserId(productionLeaderUserId) ?? source?.productionLeaderDisplayName ?? this.team?.productionLeaderDisplayName ?? null) : null,
      forwardingDelay: forwardingDelay,
      removedMachines: machines.filter(machine => machine.clientStatus === EditablePostClientStatus.Deleted).map(machine => machine.id),
      addedMachines: machines.filter(machine => machine.clientStatus === EditablePostClientStatus.New).map(machine => machine.id),
      deactivateMachinesWithoutOtherActiveTeams: false
    };
  }

  private getDisplayNameForProductionLeaderUserId(userId: string): string | null {
    const res = this.allProductionLeaders.find(pl => pl.userId === userId);

    if (!res)
      return null;

    return res.displayName;
  }

  canDeactivate(): Observable<boolean> {
    if (!this.teamHasChanged && !this.teamForm.dirty) {
      return of(true);
    }

    return new Observable<boolean>((observer) => {
      const dialogRef = this.dialog.open(SaveChangesDialogComponent, {
        data: {
          text: 'Det finns ändringar som inte är sparade. Vill du spara dem?',
          title: 'Spara ändringarna?',
          saveButton: 'Spara',
          discardButton: 'Spara inte'
        },
      });

      dialogRef.afterClosed().subscribe((result: SaveChangesResult) => {
        switch (result) {
          case SaveChangesResult.Save:
            const rawValue = this.teamForm.getRawValue() as TeamDetailsDto;
            const currentModel = this.mapFormToUpdateCommand(rawValue);
            const machines = this.teamMachinesDataSource.data ?? [];
            const shouldPromptDeactivateMachines = this.shouldPromptDeactivateMachines(currentModel, machines);

            const finalizeGuardSave = (deactivateMachines: boolean) => {
              const saveModel: UpdateTeamModel = {
                ...currentModel,
                deactivateMachinesWithoutOtherActiveTeams: deactivateMachines
              };

              const teamIsNew = this.team.id === 0;
              if (teamIsNew) {
                this.teamApiService.updateTeam(saveModel).subscribe({
                  next: (response: UpdateTeamResponseModel) => {
                    const baselineModel: UpdateTeamModel = {
                      ...saveModel,
                      deactivateMachinesWithoutOtherActiveTeams: false
                    };
                    this.onSuccessfulSave(baselineModel);
                    this.team.id = response.id;
                    this.team.teamId = response.teamId;
                    observer.next(true);
                    observer.complete();
                  },
                  error: () => {
                    observer.next(false);
                    observer.complete();
                    this.validateAllFormFields();
                  }
                });
                return;
              }

              const { patch, current } = this.createPatchOperations(saveModel);
              const sanitizedCurrent: UpdateTeamModel = {
                ...current,
                deactivateMachinesWithoutOtherActiveTeams: false
              };

              if (patch.length === 0) {
                this.onSuccessfulSave(sanitizedCurrent);
                observer.next(true);
                observer.complete();
                return;
              }

              const normalizedPatch = this.normalizePatchOperations(patch);
              const teamIdentifier = sanitizedCurrent.teamId ?? this.team.teamId;

              this.teamApiService.patchTeam(this.team.id, teamIdentifier, normalizedPatch).subscribe({
                next: () => {
                  this.onSuccessfulSave(sanitizedCurrent);
                  observer.next(true);
                  observer.complete();
                },
                error: () => {
                  observer.next(false);
                  observer.complete();
                  this.validateAllFormFields();
                }
              });
            };

            if (shouldPromptDeactivateMachines) {
              const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
                data: {
                  text: `Vill du även inaktivera de maskiner som ingår i maskinlaget ${this.team?.name}?`,
                  title: 'Inaktivera maskiner',
                  confirmButton: 'Ja',
                  cancelButton: 'Nej'
                },
                maxWidth: '90vw'
              });

              confirmDialog.afterClosed().subscribe((deactivate: boolean | undefined) => {
                if (deactivate === undefined) {
                  observer.next(false);
                  observer.complete();
                  return;
                }

                finalizeGuardSave(deactivate === true);
              });
              return;
            }

            finalizeGuardSave(false);
            break;
          case SaveChangesResult.Discard:
            observer.next(true);
            observer.complete();
            break;
          case SaveChangesResult.Cancel:
          default:
            observer.next(false);
            observer.complete();
            break;
        }
      });
    });
  }

  private validateAllFormFields() {
    Object.keys(this.teamForm.controls).forEach(field => {
      const control = this.teamForm.get(field);
      if (control) {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  private shouldPromptDeactivateMachines(currentModel: UpdateTeamModel, machines: TeamDetailsMachineInfoDto[]): boolean {
    if (!this.team || this.team.id === 0) {
      return false;
    }

    const teamWasActive = this.team.active === TeamStatus.Active;

    if (!teamWasActive || currentModel.active) {
      return false;
    }

    return this.hasActiveMachines(machines);
  }

  private hasActiveMachines(machines: TeamDetailsMachineInfoDto[]): boolean {
    return (machines ?? []).some(machine =>
      machine.machineStatus === this.machineStatus.Active &&
      machine.clientStatus !== EditablePostClientStatus.Deleted
    );
  }

  removeMachine(machine: TeamDetailsMachineInfoDto, index: number) {
    if (machine.clientStatus !== EditablePostClientStatus.New) {
      const target = this.team.machines.find(m => m.id === machine.id);
      if (target) {
        target.clientStatus = EditablePostClientStatus.Deleted;
      }
    } else {
      this.team.machines = this.team.machines.filter(m => m.id !== machine.id || m.clientStatus !== EditablePostClientStatus.New);
    }

    this.teamMachinesDataSource.data = [...this.team.machines];
    this.teamMachinesDataSource._updateChangeSubscription();
    this.teamHasChanged = true;
    this.teamForm.markAsDirty();
  }

  addMachine() {
    const dialogRef = this.dialog.open(TeamAddMachineDialogComponent, {
      data: { team: this.team },
      width: '1500px',
    });
    dialogRef.afterClosed().subscribe((machines) => {
      if (!machines || machines.length === 0) {
        return;
      }

      machines.forEach((addedMachine: TeamDetailsMachineInfoDto) => {
        const existing = this.team.machines.find(m => m.id === addedMachine.id);
        if (existing) {
          existing.clientStatus = EditablePostClientStatus.Updated;
          return;
        }

        this.team.machines.push({ ...addedMachine, clientStatus: EditablePostClientStatus.New });
      });

      this.teamMachinesDataSource.data = [...this.team.machines];
      this.teamMachinesDataSource._updateChangeSubscription();
      this.teamHasChanged = true;
      this.teamForm.markAsDirty();
    })
  }

  getRemovedMachinesCount() {
    return this.team?.machines.filter(x => x.clientStatus == EditablePostClientStatus.Deleted).map((m) => m.machineId)
  }

  navigateToContractor(event: MouseEvent, contractorId: number | null) {
    event.stopPropagation();
    this.navigationService.navigateToContractor(contractorId);
  }

  goBack() {
    if (isPlatformBrowser(this.platformId)) {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        this.router.navigate(['/teams']);
      }
    } else {
      this.router.navigate(['/teams']);
    }
  }

  teamIdExistsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const exists = this.allTeamIds.includes(control.value);
      return exists ? { teamIdExists: { value: control.value } } : null;
    };
  }

  getErrorMessage(fieldName: string): string {
    const field = this.teamForm.get(fieldName);
    if (!field || !field.errors) {
      return '';
    }

    const errorStrings: string[] = [];

    if (field.hasError('required')) {
      errorStrings.push('Obligatoriskt fält');
    }

    if (field.hasError('pattern')) {
      errorStrings.push(this.getPatternErrorMessage(fieldName));
    }

    if (fieldName === 'teamId' && field.hasError('teamIdExists')) {
      errorStrings.push('Maskinlagskoden finns redan');
    }

    return errorStrings.join(', ');
  }

  getPatternErrorMessage(fieldName: string): string {
    const patterns: Map<string, string> = new Map<string, string>([
      ['teamId', 'Maskinlagskod måste börja med "M" följt av 4 siffror (t.ex. M1234'],
      ['responsibility', 'Ansvar måste vara 4 siffror (t.ex. 1234)'],
    ]);

    return patterns.get(fieldName) || 'Ogiltigt format';
  }

  conditionalContractorValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) return null;

      const ownerType = control.parent.get('ownerType')?.value;

      if (ownerType === TeamOwnerType.External && !control.value) {
        return { required: true };
      }

      return null;
    };
  }
}

export interface UpdateTeamModel {
  id: number;
  teamId: string;
  name: string | null;
  active: boolean;
  ownerType: TeamOwnerType;
  responsibility: string | null;
  teamType: TeamType;
  productionTechnicianArea: ProductionTechnicianArea;
  contractorId: number | null;
  productionLeaderUserId: string | null;
  productionLeaderDisplayName: string | null;
  forwardingDelay: number | null;
  removedMachines: number[];
  addedMachines: number[];
  deactivateMachinesWithoutOtherActiveTeams: boolean;
}
